import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ShieldWarning, ClockCounterClockwise, Warning, XCircle } from "@phosphor-icons/react";

const STATUS_INFO = {
  "Registration Submitted": {
    icon: ClockCounterClockwise, tone: "amber",
    heading: "Your registration is in the queue",
    body: "Thanks for signing up. Our team will begin reviewing your application shortly.",
  },
  "KYC Pending": {
    icon: ClockCounterClockwise, tone: "amber",
    heading: "KYC review in progress",
    body: "We're verifying your identity and firm details. This typically takes 1–2 business days.",
  },
  "Classification Pending": {
    icon: ShieldWarning, tone: "amber",
    heading: "Awaiting Professional Investor classification",
    body: "Our compliance team is assessing whether you qualify as a Per Se or Elective Professional Investor under FCA rules.",
  },
  "Compliance Review": {
    icon: ShieldWarning, tone: "amber",
    heading: "Final compliance review",
    body: "Your account is with our FCA compliance partner (Infinity Asset Management LLP) for final sign-off.",
  },
  "Suspended": {
    icon: Warning, tone: "red",
    heading: "Account suspended",
    body: "Your account has been suspended pending further review. Please contact compliance@inventya.com.",
  },
  "Rejected": {
    icon: XCircle, tone: "red",
    heading: "Access not granted",
    body: "Following review, we're unable to grant access to the platform at this time. Please contact compliance@inventya.com if you believe this is in error.",
  },
};

export default function AccessGate({ children }) {
  const { user, logout, isClient, isTeam } = useAuth();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    // Non-investors bypass the gate entirely
    if (user.role !== "investor") { setAccess({ approved: true }); setLoading(false); return; }
    api.get("/me/access")
      .then((r) => setAccess(r.data))
      .catch(() => setAccess({ approved: false, approval_status: "Compliance Review" }))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading || !user) return children;
  if (user.role !== "investor" || isTeam || isClient) return children;
  if (access?.approved) return children;

  const info = STATUS_INFO[access?.approval_status] || STATUS_INFO["Compliance Review"];
  const Icon = info.icon;
  const toneCls = info.tone === "red"
    ? "border-red-200 dark:border-red-900 bg-red-50/60 dark:bg-red-950/30 text-red-700 dark:text-red-300"
    : "border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16" data-testid="access-gate">
      <div className="max-w-xl w-full">
        <div className={`rounded-2xl border p-8 ${toneCls}`}>
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-lg bg-white/60 dark:bg-black/20 grid place-items-center shrink-0">
              <Icon size={22} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70 mb-1.5">
                {access?.approval_status || "Compliance review"}
              </div>
              <h2 className="font-display text-2xl font-bold mb-2 leading-tight">{info.heading}</h2>
              <p className="text-sm leading-relaxed opacity-90">{info.body}</p>
              <p className="text-xs mt-4 opacity-75">
                Your account is currently under compliance review. Access to investment opportunities will be enabled
                once your Professional Investor status has been approved.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-6 text-center text-xs text-muted-foreground">
          Signed in as <b>{user.email}</b> —{" "}
          <button onClick={logout} className="text-emerald-700 dark:text-emerald-400 hover:underline">sign out</button>
        </div>
      </div>
    </div>
  );
}
